import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CircleAlert, Loader2 } from '../../icons.js';
import { Button } from '../../atoms/Button/Button.js';
import { cn } from '../../lib/cn.js';
import { useTranslate } from '../../lib/i18n.js';
/**
 * What the generation is doing, and what to do when it did not work.
 *
 * ── Why there is no progress bar ─────────────────────────────────────────
 *
 * `ExtractionStatus` has one because extraction has something to count: pages
 * finished, out of pages sent. Generation is a single request that reports
 * nothing on its way, so a bar creeping across it would be an animation
 * impersonating a measurement — and it would be lying about the one number a
 * tutor would read it for. A spinner and a sentence say the true thing: this
 * is running, and here is what it is doing.
 *
 * Invalid model output is a failed delivery: the API neither stores nor
 * returns it, so the app supplies a localized failure message here.
 */
export function GenerationStatus({ state, onRetry, className }) {
    const t = useTranslate();
    if (state.status === 'idle')
        return null;
    const panel = 'flex flex-col gap-3 rounded-md border-[1.5px] p-6';
    if (state.status === 'running') {
        return (_jsxs("div", { className: cn(panel, 'border-border bg-background', className), role: "status", "aria-live": "polite", children: [_jsxs("p", { className: "flex items-center gap-3 text-h4 font-semibold text-text", children: [_jsx(Loader2, { "aria-hidden": "true", className: "size-icon-md shrink-0 animate-spin" }), t('practice.automatic.generating')] }), _jsx("p", { className: "text-body-sm text-text-secondary", children: t('practice.automatic.generatingBody') })] }));
    }
    /**
     * Nothing at all, deliberately.
     *
     * A green "Your practice is ready" panel used to sit here, above the
     * practice it was announcing. It said what the thing underneath it was
     * already saying, and it said it in the space the practice wanted: a tutor
     * who asked for a practice should land on the practice, not on a banner
     * about one. The generated-practice section is the success state.
     *
     * Still a distinct status rather than another name for `idle`, so the run
     * can announce success before the parent navigation completes.
     */
    if (state.status === 'done')
        return null;
    return (_jsxs("div", { className: cn(panel, 'border-error-border bg-error-light', className), children: [_jsxs("h3", { className: "flex items-center gap-2 text-h4 font-semibold text-error-text", children: [_jsx(CircleAlert, { "aria-hidden": "true", className: "size-icon-md shrink-0" }), t('practice.automatic.generationFailedHeading')] }), _jsx("p", { className: "text-body-sm text-text", children: state.message }), onRetry ? (_jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [_jsx(Button, { variant: "secondary", onClick: onRetry, children: t('practice.automatic.retry') }), _jsx("p", { className: "text-body-sm text-text-secondary", children: t('practice.automatic.retryHint') })] })) : null] }));
}
//# sourceMappingURL=GenerationStatus.js.map